import { sorted } from "helpers";
import { Loaded } from "models/loaded";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface Chunk {
  start: number;
  end: number;
}
export type ChunkData<T> = Chunk & { value: Loaded<T> };

export const useLoadedWindow = <T,>(
  window: Chunk,
  get: (chunk: Chunk) => Promise<T>
) => {
  const [chunks, setChunks] = useState<ChunkData<T>[]>([]);

  const getChunk = (chunk: Chunk) => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect -- async chunk loading state machine
    setChunks((current) =>
      sorted(
        [...current, { ...chunk, value: { status: "loading" } }],
        (chunk) => chunk.start
      )
    );
    void get(chunk).then((data) => {
      setChunks((current) =>
        current.map((item) =>
          item.start === chunk.start && item.end === chunk.end
            ? {
                ...chunk,
                value: { status: "success", data, fetching: false },
              }
            : item
        )
      );
    });
  };

  useEffect(() => {
    if (chunks.length > 0) {
      // means we already have some data, otherwise the other hook will sort it out
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- async chunk reload clears stale data
      setChunks([]); // this is necessary to clear "bad data"
      getChunk(window);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- chunks/window intentionally excluded: only re-init when the fetch function changes
  }, [get]);

  useEffect(() => {
    // If/when performance becomes an issue, unused chunks (ie. those that have
    // no overlap with the start and end) can be removed
    missingChunks(window, chunks).forEach(getChunk);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- chunks and getChunk intentionally excluded: getChunk writes to chunks, including either causes infinite loop
  }, [window.start, window.end]);

  const refetchAllChunks = useCallback(() => {
    setChunks([]);
    missingChunks(window, chunks).forEach(getChunk);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- chunks and getChunk captured at call time; including them would create stale closure issues
  }, [window.start, window.end]);

  return { chunks, refetchAllChunks };
};

const missingChunks = (window: Chunk, chunks: Chunk[]): Chunk[] => {
  if (window.start >= window.end) {
    return [];
  } else if (chunks.length === 0) {
    return [window];
  } else {
    const [chunk, ...rest] = chunks;
    if (!chunk) return [window];

    return [
      { ...window, end: Math.min(window.end, chunk.start) },
      ...missingChunks(
        { ...window, start: Math.max(window.start, chunk.end) },
        rest
      ),
    ].filter((item) => item.start < item.end);
  }
};

export const useMergedChunks = <T extends { timestamp: Date }>(
  chunks: ChunkData<Record<string, T[]>>[]
) =>
  useMemo(() => {
    const merged = mergeObjects(
      chunks.flatMap((chunk) =>
        chunk.value.status === "success" ? [chunk.value.data] : []
      ),
      (left, right) => [...(left ?? []), ...(right ?? [])]
    );

    return Object.fromEntries(
      Object.entries(merged).map(([k, arr]) => [
        k,
        [...arr].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()),
      ])
    );
  }, [chunks]);

const mergeObjects = <T,>(
  objects: Record<string, T>[],
  merge: (left: T | undefined, right: T | undefined) => T
): Record<string, T> => {
  return objects.reduce(
    (left, right) =>
      Object.fromEntries(
        [...Object.keys(left), ...Object.keys(right)].map((key) => [
          key,
          merge(left[key], right[key]),
        ])
      ),
    {}
  );
};

/**
 * Force the bounds of a window to snap to discrete points in time.
 *
 * This is helpful when you want to avoid sending a whole query to load data
 * resulting from a small change in the window.  This will make sure you always
 * load a little bit more than needed, so small changes in the window will
 * usually not trigger a whole new query.
 *
 * A buffer can be added to either side; in this case, that number of chunks
 * will be loaded on either end in addition to what is strictly necessary.
 */
export const quantisedWindow = (
  window: Chunk,
  chunkSize: number,
  chunksBuffer: number
): Chunk => {
  const startChunkIndex = Math.floor(window.start / chunkSize);
  const endChunkIndex = Math.ceil(window.end / chunkSize);
  return {
    start: (startChunkIndex - chunksBuffer) * chunkSize,
    end: (endChunkIndex + chunksBuffer) * chunkSize,
  };
};

export const usePreventFuture = (chunk: Chunk, lagSeconds = 10): Chunk => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const frequency = 0.2;
    const interval = window.setInterval(() => {
      setNow(new Date());
    }, 1000 / frequency);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return {
    start: chunk.start,
    end: Math.min(chunk.end, now.getTime() - lagSeconds * 1000),
  };
};

export const adjustTimestamp = (timestamp: Date, seconds: number) =>
  new Date(timestamp.getTime() + seconds * 1000);
